import { Component, inject, signal, effect, untracked, computed, ViewChild, input, ChangeDetectionStrategy } from '@angular/core';
import {
  CdkDragDrop,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  reorderLayoutEditorItemsOnVisibilityToggle,
} from '../../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  DateRangePickerOutput,
  DateRangePickerValue,
} from '../../../../shared/components/simple-date-picker/normal-date-picker.component';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import { UnitSuggestionService } from '../../../../core/services/unit-suggestion.service';
import { IndividualToasterComponent } from '../../../../shared/components/loader/loader.component';
import { DashboardCardsComponent } from '../../../../shared/components/ui/dashboard-cards/dashboard-cards.component';
import { CommonModule } from '@angular/common';
import {
  ScrollTableComponent,
  TableHeader,
} from '../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { MultipleBarComponent } from '../../../../shared/components/charts/bar-charts/multiple-bar/multiple-bar.component';
import { Theme, ThemeService } from '../../../../core/services/theme.service';
import { MultiBarGridChartComponent } from '../../../../shared/components/charts/bar-charts/multi-bar-grid-chart/multi-bar-grid-chart';
import { ApiService } from '../../../../core/services/api.service';
import { NormalDatePickerComponent } from '../../../../shared/components/simple-date-picker/normal-date-picker.component';
import { PredefinedRange } from '../../../../shared/components/simple-date-picker/normal-date-picker.component';
import { SlidePanelComponent } from '../../../../shared/components/slide-panel/slide-panel.component';
import { InputComponent } from '../../../../shared/components/ui/input/input.component';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { DropdownComponent } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { ToasterService } from '../../../../core/services/toaster.service';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { environment } from '../../../../../environments/environment';
import { AuditLogPanelComponent } from '../../../../shared/components/audit-log-panel/audit-log-panel.component';
import { AuditEntityType } from '../../../../core/models/audit-log.model';
import { DashboardPageKey, CampaignAdsStatsSectionId, CampaignAdsStatsSectionDef } from '../../../../core/enums/dashboard-cards.enum';
import { CustomDashboardPinService } from '../../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../../core/services/customize-layout-batch.service';
import { createEmbeddedSectionFilter, findCatalogPageId, resolveActiveCatalogProductId, stripProductPrefix } from '../../../../shared/components/dashboard/dashboard-embed.helpers';
import { AiHeadlinesComponent } from '../../../../shared/components/ai-headlines/ai-headlines.component';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(quarterOfYear);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

/** Persisted layout state per section (visibility + width + order). */
interface SectionLayoutItem {
  id: CampaignAdsStatsSectionId;
  visible: boolean;
  /** 4 | 6 | 8 | 12 — proportional width inside the 12-col dashboard grid. */
  colSpan: number;
  order: number;
}

/** Draft row used by the inline Customize Layout editor. Carries the
 *  section title alongside the persisted layout fields so the popup tile
 *  can render its label without re-looking-up `SECTIONS`. */
interface LayoutEditorItem extends SectionLayoutItem {
  title: string;
}

import { LayoutEditorOverlayDirective } from '../../../../shared/components/dashboard/layout-editor-overlay.directive';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';

@Component({
  selector: 'app-campaign-ads-stats',
  templateUrl: './campaign-ads-stats.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    LayoutEditorOverlayDirective,
    ReactiveFormsModule,
    IndividualToasterComponent,
    ScrollTableComponent,
    MultiBarGridChartComponent,
    NormalDatePickerComponent,
    SlidePanelComponent,
    InputComponent,
    ButtonComponent,
    DropdownComponent,
    IconComponent,
    AlertComponent,
    AuditLogPanelComponent,
    DragDropModule,
    AiHeadlinesComponent,
    IconComponentV2,
  ],
})
export class CampaignAdsStatsComponent {
  @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;
  private unitService = inject(UnitSuggestionService);
  private apiService = inject(ApiService);
  private sanitizer = inject(DomSanitizer);
  private toasterService = inject(ToasterService);
  private productConfigService = inject(ProductConfigService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);

  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);

  readonly PAGE_KEY = DashboardPageKey.CAMPAIGN_ADS_STATS;
  readonly SectionId = CampaignAdsStatsSectionId;
  readonly SECTIONS = CampaignAdsStatsSectionDef;

  /**
   * Section ids that must NEVER appear in the Customize Layout editor or in
   * the drag-drop / Custom Dashboard flow. Audit Log is rendered as a
   * standalone block below the customizable grid, so it's filtered out of
   * `layoutMap`, `editorItems`, and the defaults reset.
   */
  private readonly LAYOUT_EXCLUDED_SECTION_IDS: ReadonlySet<CampaignAdsStatsSectionId> =
    new Set<CampaignAdsStatsSectionId>([CampaignAdsStatsSectionId.AUDIT_LOG]);

  /** SECTIONS minus any section excluded from the customizable layout. */
  private readonly LAYOUT_SECTIONS = CampaignAdsStatsSectionDef.filter(
    (s) => !this.LAYOUT_EXCLUDED_SECTION_IDS.has(s.id as CampaignAdsStatsSectionId),
  );

  unit = signal<string>('day');
  isPrevious: boolean = false;
  isLoading = signal<boolean>(false);
  start_date = dayjs().tz('America/Los_Angeles').subtract(89, 'day').startOf('day').format('YYYY-MM-DD');
  end_date = dayjs().tz('America/Los_Angeles').endOf('day').format('YYYY-MM-DD');
  headers = signal<TableHeader[]>([]);
  auditEntityTypes = [AuditEntityType.CAMPAIGN_METRIC];
  productPid = computed(() => {
    const id = this.productId();
    const products = this.apiService.products();
    const product = products.find((p) => p.id === id);
    return product?.pid || null;
  });

  // Store the latest list data to calculate totals for cards
  private latestEnquiryList: any[] = [];

  // FIX 1: Initialize signal with the ACTUAL current ID immediately.
  productId = signal<string>(this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id') || '');
  /** Tags in-flight fetches so stale responses are ignored after product changes. */
  private dataFetchGeneration = 0;

  // Chart data (will be populated from API)
  processedData1: any[] = [];
  processedData2: any[] = [];
  processedData3: any[] = [];
  customLegendData: any[] = [];

  private currentTheme: Theme = 'light';
  isDark: boolean = this.currentTheme === 'dark';

  // Date Picker Logic
  LA_TIMEZONE = 'America/Los_Angeles';
  nowInLA = () => dayjs().tz(this.LA_TIMEZONE);

  metricsLabel: string = 'Last 3 Months Campaign Metrics';

  datePickerValue: DateRangePickerValue = {
    startDate: dayjs().tz(this.LA_TIMEZONE).subtract(89, 'day').startOf('day').toISOString(),
    endDate: dayjs().tz(this.LA_TIMEZONE).endOf('day').toISOString(),
    unit: 'month',
    comparisonType: 'none',
    presetLabel: 'Last 3 Months'
  };

  get selectedStartRange() {
    return this.nowInLA().subtract(89, 'day').startOf('day').toISOString();
  }

  get selectedEndRange() {
    return this.nowInLA().endOf('day').toISOString();
  }

  predefinedRanges: PredefinedRange[] = [
    {
      label: 'Last 90 Days',
      getValue: () => ({
        start: this.nowInLA().subtract(89, 'day').startOf('day').toISOString(),
        end: this.nowInLA().endOf('day').toISOString(),
      }),
    },
    {
      label: 'This Quarter',
      getValue: () => {
        const now = this.nowInLA();
        const quarter = Math.floor(now.month() / 3);
        const quarterStart = now.month(quarter * 3).startOf('month');
        return {
          start: quarterStart.toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Quarter',
      getValue: () => {
        const now = this.nowInLA().subtract(3, 'month');
        const quarter = Math.floor(now.month() / 3);
        const quarterStart = now.month(quarter * 3).startOf('month');
        const quarterEnd = now.month(quarter * 3 + 2).endOf('month');
        return {
          start: quarterStart.toISOString(),
          end: quarterEnd.toISOString(),
        };
      },
    },
    {
      label: 'This Year',
      getValue: () => {
        const now = this.nowInLA();
        return {
          start: now.startOf('year').toISOString(),
          end: now.endOf('day').toISOString(),
        };
      },
    },
    {
      label: 'Last Year',
      getValue: () => {
        const lastYear = this.nowInLA().subtract(1, 'year');
        return {
          start: lastYear.startOf('year').toISOString(),
          end: lastYear.endOf('year').toISOString(),
        };
      },
    },
  ];

  // Visibility Controls
  showXLabels: boolean = true;
  showYLabels: boolean = true;
  showBarValues: boolean = true;

  // Form & Panel Logic
  private fb = inject(FormBuilder);

  statsForm: FormGroup;
  panelOpen = signal(false);
  isEditing = signal(false);
  errorMessage = signal('');
  successMessage = signal('');

  currentYear = this.nowInLA().year();
  currentMonth = this.nowInLA().month() + 1;

  dropdownMinYear = 2018;
  dropdownMaxYear = this.currentYear;
  dropdownMinMonth?: number;
  dropdownMaxMonth?: number = this.currentMonth;

  constructor(private themeService: ThemeService) {
    this.statsForm = this.fb.group({
      id: [null], // Added hidden id field
      date: [null, Validators.required],
      cost: [null, [Validators.required, Validators.min(0)]],
      budget: [null, [Validators.required, Validators.min(0)]],
    });

    effect(() => {
      // Get the latest ID from service/storage
      const newProductId =
        this.apiService.getSelectedProductId() ||
        sessionStorage.getItem('selected_product_id');

      if (newProductId) {
        untracked(() => {
          // FIX 2: Only trigger logic if the Product ID has CHANGED.
          if (this.productId() !== newProductId) {
            this.dataFetchGeneration += 1;
            this.productId.set(newProductId);

            if (this.start_date && this.end_date) {
              this.loadCombinedMetrics();
              this.loadOverallPerformance();
            }
          }
        });
      }
    });

    effect(() => {
      this.themeService.theme();
      this.updateChartColors();
    });

    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<CampaignAdsStatsSectionId>();
          keys.forEach((k) => {
            if (Object.values(CampaignAdsStatsSectionId).includes(k as CampaignAdsStatsSectionId)) {
              pinned.add(k as CampaignAdsStatsSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    // Pre-resolve catalog `widgets.id` per section so per-click adds can
    // skip the catalog round-trip (mirrors the Social Platform / engine
    // pattern). The resolved map drives the fast `toggleLayoutItem`
    // path in `addToCustomDashboard` — when a section's id isn't in the
    // map yet (catalog still loading), the click gracefully falls back
    // to the existing `toggleSection` flow so behaviour is preserved.
    //
    // Sections in `NEVER_PINNABLE_SECTIONS` (Audit Log) are filtered
    // out here too so they can never be sent to the dashboards API
    // even if the backend catalog ever starts advertising them.
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<CampaignAdsStatsSectionId, number>();
            for (const sec of Object.values(CampaignAdsStatsSectionId)) {
              if (this.NEVER_PINNABLE_SECTIONS.has(sec)) continue;
              const id = widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });

    this.statsForm.get('date')?.valueChanges.subscribe((d) => {
      if (!this.panelOpen()) return;

      const productPid = this.apiService.getSelectedProductPid();
      if (!productPid || !d || !d.year || !d.month) {
        return;
      }

      const formattedDate = `${d.year}-${String(d.month).padStart(2, '0')}`;

      this.isLoading.set(true);
      this.apiService.get('/campaign-metrics/by-date', {
        startDate: formattedDate,
        endDate: formattedDate,
        productPid: productPid
      }).subscribe({
        next: (res: any) => {
          this.isLoading.set(false);
          const data = res?.data || res;
          const record = Array.isArray(data) ? data[0] : (data?.campaignMetrics?.[0] || data?.[0] || data);

          if (record && record.pid) {
            this.statsForm.patchValue({
              id: record.pid,
              cost: record.cost ?? 0,
              budget: record.budget ?? 0
            }, { emitEvent: false });
          } else {
            this.statsForm.patchValue({
              id: null,
              cost: null,
              budget: null
            }, { emitEvent: false });
          }
        },
        error: (err) => {
          this.isLoading.set(false);
          console.error('Error checking campaign metrics by date on change', err);
          this.statsForm.patchValue({
            id: null,
            cost: null,
            budget: null
          }, { emitEvent: false });
        }
      });
    });
  }

  openPanel() {
    this.dropdownMinYear = 2018;
    this.dropdownMaxYear = this.currentYear;
    this.dropdownMinMonth = undefined;
    this.dropdownMaxMonth = this.currentMonth;
    this.isEditing.set(false);

    let defaultDate: any = null;
    if (this.unit() === 'month' && this.start_date) {
      const d = dayjs(this.start_date);
      if (d.isValid()) {
        defaultDate = { year: d.year(), month: d.month() + 1 };
      }
    }

    this.statsForm.reset({ date: defaultDate, cost: null, budget: null });
    this.statsForm.get('date')?.enable();
    this.panelOpen.set(true);
  }

  closePanel() {
    this.panelOpen.set(false);
    this.statsForm.reset();
    this.dropdownMinYear = 2018;
    this.dropdownMaxYear = this.currentYear;
    this.dropdownMinMonth = undefined;
    this.dropdownMaxMonth = this.currentMonth;
  }

  getErrorMessage(controlName: string): string {
    const control = this.statsForm.get(controlName);
    if (control && !control.touched && !control.dirty) return '';
    if (control?.hasError('required')) return `${controlName} is required`;
    if (control?.hasError('min')) return `Minimum value is ${control.errors?.['min'].min}`;
    if (control?.hasError('max')) return `Maximum value is ${control.errors?.['max'].max}`;
    return '';
  }

  resolveErrorMessage(err: any): string {
    if (typeof err?.error === 'string') return err.error;
    if (typeof err?.error?.error === 'string') return err.error.error;
    if (typeof err?.error?.message === 'string') return err.error.message;
    return err?.message || 'An unexpected error occurred';
  }

  submitStats() {
    if (this.statsForm.invalid) {
      this.statsForm.markAllAsTouched();
      return;
    }

    const productId = this.apiService.getSelectedProductId();
    const productPid = this.apiService.getSelectedProductPid();

    if (!productId) {
      this.toasterService.error('Product ID is missing');
      return;
    }

    if (!productPid) {
      this.toasterService.error('Product PID is missing');
      return;
    }

    const formValue = this.statsForm.getRawValue();

    // Format date from object to YYYY-MM
    let dateStr = '';
    const d = formValue.date;
    if (d && typeof d === 'object' && d.year && d.month) {
      dateStr = `${d.year}-${String(d.month).padStart(2, '0')}`;
    } else {
      console.error('Invalid date format');
      return;
    }

    const payload: any = {
      productPid: productPid,
      date: dateStr,
      cost: formValue.cost,
      budget: formValue.budget
    };

    if (this.isEditing() && formValue.id) {
      payload.pid = formValue.id;
    }

    this.isLoading.set(true);
    this.apiService.post('/campaign-metrics', payload).subscribe({
      next: (res) => {
        this.toasterService.success(this.isEditing() ? 'Stats updated successfully' : 'Stats added successfully');
        this.successMessage.set(this.isEditing() ? 'Stats updated successfully' : 'Stats added successfully');
        this.isLoading.set(false);
        this.closePanel();

        // Refresh all data
        this.loadCombinedMetrics();
        this.loadOverallPerformance();
        this.auditLogPanel?.reloadLogs();

        setTimeout(() => this.successMessage.set(''), 3000);
      },
      error: (err) => {
        console.error(err);
        const resolvedMsg = this.resolveErrorMessage(err);
        this.toasterService.error(resolvedMsg);
        this.errorMessage.set(resolvedMsg);
        this.isLoading.set(false);
        setTimeout(() => this.errorMessage.set(''), 5000);
      }
    });
  }

  ngOnInit(): void {
    this._initLayout();
  }

  // ─── Section layout state (visibility + colSpan + order) ──────────────
  /** Persisted layout map driving the actual rendered page. */
  readonly layoutMap = signal<Record<CampaignAdsStatsSectionId, SectionLayoutItem>>(
    {} as Record<CampaignAdsStatsSectionId, SectionLayoutItem>,
  );

  /** All sections sorted by their persisted order. */
  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  /** Sections currently visible, in order — used by the live grid. */
  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  /** Standalone page: all visible sections; embedded: allow-list from parent. */
  readonly embeddedVisibleSections = createEmbeddedSectionFilter(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.orderedSections,
  );

  /**
   * Sections currently rendered by the host. On the standalone page
   * (`mixedMode() === false`) every section is active. When mounted
   * inside `WidgetHostComponent` only the cards matching
   * `visibleWidgetKeys` are active — used to gate data loaders below.
   *
   * IMPORTANT: derived directly from `visibleWidgetKeys()` so it is
   * available synchronously on first paint; using the layout-derived
   * `embeddedVisibleSections()` would be empty before the layout loads
   * and would cause every guarded loader to short-circuit.
   */
  private readonly activeSectionIds = computed<ReadonlySet<string>>(() => {
    const keys = this.visibleWidgetKeys();
    if (!keys || keys.length === 0) return new Set();
    const allowed = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      allowed.add(k);
      const stripped = stripProductPrefix(k);
      if (stripped !== k) allowed.add(stripped);
    }
    return allowed;
  });

  private isSectionActive(...sectionIds: CampaignAdsStatsSectionId[]): boolean {
    if (!this.mixedMode()) return true;
    const active = this.activeSectionIds();
    if (active.size === 0) return true;
    return sectionIds.some((id) => active.has(id));
  }

  isVisible(sectionId: CampaignAdsStatsSectionId): boolean {
    return this.layoutMap()[sectionId]?.visible ?? true;
  }

  /** Tailwind col-span class for a section in the live 12-col grid. */
  getSectionColClass(item: SectionLayoutItem | CampaignAdsStatsSectionId): string {
    const id = typeof item === 'string' ? item : item.id;
    const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
    switch (span) {
      case 4: return 'col-span-12 lg:col-span-4';
      case 6: return 'col-span-12 lg:col-span-6';
      case 8: return 'col-span-12 lg:col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  trackSection = (_idx: number, sec: SectionLayoutItem): string => sec.id;

  // ─── "Pin to Custom Dashboard" (API-backed, via CustomDashboardPinService) ───
  readonly pinnedSections = signal<Set<CampaignAdsStatsSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<CampaignAdsStatsSectionId>>(new Set());

  /**
   * Sections that are explicitly excluded from the Custom ("My")
   * Dashboard by product rule, regardless of whether the backend
   * catalog advertises them. The audit trail is a page-local
   * diagnostic surface — it doesn't belong on the user's personalized
   * dashboard, so we never offer the pin button for it (and never
   * seed it into the mixed dashboard).
   *
   * Defensive denylist that runs IN ADDITION TO the catalog gate:
   * if a future backend release ever starts advertising
   * `ca_audit_log` in the catalog, the section will still be
   * unpinnable on the frontend.
   */
  private readonly NEVER_PINNABLE_SECTIONS: ReadonlySet<CampaignAdsStatsSectionId> =
    new Set<CampaignAdsStatsSectionId>([CampaignAdsStatsSectionId.AUDIT_LOG]);

  /**
   * Catalog `widgets.id` resolved per section, keyed by canonical
   * `CampaignAdsStatsSectionId`. Populated by the constructor effect
   * once the catalog products + page id resolve, and consumed by
   * {@link addToCustomDashboard} so per-click pins use the
   * pre-resolved id with {@link CustomDashboardPinService.toggleLayoutItem}
   * (same fast path Social Platform / Monthly Report 1 / 2 use —
   * one POST/PATCH per click; never the whole page).
   *
   * Sections in {@link NEVER_PINNABLE_SECTIONS} are stripped before
   * the map is set so they never become pinnable even if the
   * backend catalog starts advertising them.
   */
  readonly pinnableWidgetIds = signal<Map<CampaignAdsStatsSectionId, number>>(
    new Map(),
  );

  /**
   * Normalizes the section input to its string id. The template iterates
   * `embeddedVisibleSections()` and passes the full `SectionLayoutItem`
   * object (`sec`), while TS callers sometimes pass the raw enum value.
   * Without this normalization, `pinnedSections().has(<object>)` against
   * a string-keyed `Set` would always return `false` and the "Added"
   * pill / "Adding…" spinner would never light up — making a successful
   * pin look like a no-op.
   */
  private _toSectionId(
    sec: { id: CampaignAdsStatsSectionId } | CampaignAdsStatsSectionId,
  ): CampaignAdsStatsSectionId {
    return typeof sec === 'object' && sec !== null ? sec.id : sec;
  }

  isAddedToCustomDashboard(
    sec: { id: CampaignAdsStatsSectionId } | CampaignAdsStatsSectionId,
  ): boolean {
    return this.pinnedSections().has(this._toSectionId(sec));
  }

  isAddingToCustomDashboard(
    sec: { id: CampaignAdsStatsSectionId } | CampaignAdsStatsSectionId,
  ): boolean {
    return this.pendingPinSections().has(this._toSectionId(sec));
  }

  /**
   * Returns `true` when the given section is allowed AND has a
   * catalog `widgets.id` we can send to the dashboards API.
   *
   * Two gates run in order:
   *   1. {@link NEVER_PINNABLE_SECTIONS} — product-level denylist
   *      (Audit Log). ALWAYS returns `false`, regardless of catalog
   *      state.
   *   2. {@link pinnableWidgetIds} — backend catalog presence.
   *      Sections still loading return `false` here so callers can
   *      hide UI affordances for known-failing actions.
   *
   * Kept as a public method for parity with Social Platform — the
   * current Campaign Ads template doesn't gate the pin button on
   * this (the section list is already filtered to layout members,
   * and the catalog typically resolves before the user can click),
   * but exposing it lets the template add the same gate later
   * without further refactors.
   */
  canPinToCustomDashboard(
    sec: { id: CampaignAdsStatsSectionId } | CampaignAdsStatsSectionId,
  ): boolean {
    const sectionId = this._toSectionId(sec);
    if (this.NEVER_PINNABLE_SECTIONS.has(sectionId)) return false;
    return this.pinnableWidgetIds().has(sectionId);
  }

  /**
   * Quick-action toggle handler. Mirrors
   * `SocialPlatformComponent.addToCustomDashboard`:
   *   • When the catalog `widgets.id` for the section is already
   *     resolved (the constructor effect populated
   *     {@link pinnableWidgetIds}), forward the pre-resolved
   *     numeric id into
   *     {@link CustomDashboardPinService.toggleLayoutItem}. The
   *     service performs the existence probe + `PATCH /visibility`
   *     or seed `POST /dashboards`. Only ONE widget id is sent —
   *     the dashboard never receives the whole page on a single
   *     click. This is the same fast path Social Platform /
   *     Monthly Report 1 / 2 use.
   *   • When the catalog hasn't resolved yet (rare — typically only
   *     in the brief window after first page load), gracefully fall
   *     back to the existing {@link CustomDashboardPinService.toggleSection}
   *     flow which performs the catalog lookup on click. This
   *     preserves the page's existing click-to-pin behaviour 1:1
   *     so no functionality is hampered.
   *
   * Sections in {@link NEVER_PINNABLE_SECTIONS} (Audit Log) are
   * blocked here as defense in depth, even though the current
   * template renders Audit Log outside the layout grid so the
   * button never appears for it.
   */
  addToCustomDashboard(
    sec: { id: CampaignAdsStatsSectionId } | CampaignAdsStatsSectionId,
  ): void {
    const sectionId = this._toSectionId(sec);
    if (!sectionId) return;
    if (this.NEVER_PINNABLE_SECTIONS.has(sectionId)) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) return;

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    // Fast path — catalog `widgets.id` already resolved, so we can
    // forward it directly and skip the per-click catalog lookup.
    const widgetId = this.pinnableWidgetIds().get(sectionId);
    if (widgetId != null) {
      this.customDashboardPin.toggleLayoutItem({
        pageId,
        widgetId,
        sourcePageId: pageId,
        colSpan,
        rowSpan: 1,
        customConfig: {},
        productId: resolveActiveCatalogProductId(this.apiService),
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
      return;
    }

    // Fallback path — catalog still resolving. Preserve the
    // page's existing behaviour by doing the lookup on click.
    this.customDashboardPin.toggleSection({
      pageId,
      widgetKey: sectionId,
      colSpan,
      isCurrentlyPinned,
      onPinnedChange,
      onPendingChange,
    });
  }

  // ─── Customize Layout editor (mini-dashboard preview) ──────────────────
  /** State for the inline Customize Layout editor. */
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<LayoutEditorItem[]>([]);

  /** Number of cards currently marked visible in the editor draft. */
  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  trackEditorItem = (_idx: number, item: LayoutEditorItem): string => item.id;

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /** localStorage key for this page's persisted layout. */
  private readonly LAYOUT_LS_KEY = 'prism_campaign_ads_stats_layout_v1';

  /** Default col-span per section — matches the historical full-width layout
   *  where every section was stacked at 12/12.
   *
   *  Only includes sections that participate in the customizable grid; AUDIT_LOG
   *  is rendered as a standalone block and is intentionally excluded. */
  private readonly DEFAULT_COL_SPAN: Partial<Record<CampaignAdsStatsSectionId, number>> = {
    [CampaignAdsStatsSectionId.STATS_OVERVIEW]: 12,
    [CampaignAdsStatsSectionId.MOM_COMPARISON]: 12,
    [CampaignAdsStatsSectionId.MONTHLY_ENQUIRIES]: 12,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: LayoutEditorItem[] = this.orderedSections()
      .filter((s) => !this.LAYOUT_EXCLUDED_SECTION_IDS.has(s.id))
      .map((s) => ({
        id: s.id,
        title: titleById.get(s.id) ?? s.id,
        visible: s.visible,
        colSpan: this._normalizeColSpan(s.colSpan),
        order: s.order,
      }));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: CampaignAdsStatsSectionId): void {
    // When a card is hidden, push it to the bottom so the visible cards
    // reflow into the freed grid slot. When shown again, slot it back in
    // just above the first hidden item so it rejoins the visible group.
    this.editorItems.update((items) =>
      reorderLayoutEditorItemsOnVisibilityToggle(items, id),
    );
  }

  /** Restores the editor draft to the page's default order, widths, and visibility. */
  resetLayoutEditorToDefaults(): void {
    const defaults: LayoutEditorItem[] = this.LAYOUT_SECTIONS.map((s, idx) => ({
      id: s.id as CampaignAdsStatsSectionId,
      title: s.title,
      visible: true,
      colSpan: this._normalizeColSpan(
        this.DEFAULT_COL_SPAN[s.id as CampaignAdsStatsSectionId] ?? 12,
      ),
      order: idx,
    }));
    this.editorItems.set(defaults);
  }

  /** Persists the editor draft to local layout state + localStorage. */
  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout: Record<CampaignAdsStatsSectionId, SectionLayoutItem> =
      {} as Record<CampaignAdsStatsSectionId, SectionLayoutItem>;
    draft.forEach((d, idx) => {
      layout[d.id] = {
        id: d.id,
        visible: d.visible,
        colSpan: this._normalizeColSpan(d.colSpan),
        order: idx,
      };
    });
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);

    // Best-effort backend persistence — see {@link _persistLayoutToBackend}
    // for the fast/slow path branching. Local state above is unchanged
    // regardless of whether the network call succeeds.
    this._persistLayoutToBackend(draft);

    this.isLayoutEditorOpen.set(false);
  }

  /**
   * Writes the editor draft to the user's per-page personalized
   * dashboard via {@link CustomizeLayoutBatchService}.
   *
   * Two paths, picked dynamically per click so the API call always
   * fires regardless of whether the reactive catalog effects in the
   * constructor have hydrated yet:
   *
   *   1. **Fast path** — `effectiveCatalogPageId()` AND
   *      `pinnableWidgetIds()` are both populated (catalog already
   *      cached). Mirrors Social Platform: maps each draft row to
   *      its pre-resolved `widgets.id` and calls
   *      `applyEditorDraftToPageDashboard` directly. Audit-Log-style
   *      sections with no backend widget are dropped.
   *
   *   2. **Slow / fallback path** — neither cached yet (very first
   *      Apply on a fresh load before `selectedProductId()` signal
   *      has fired, or catalog GET still in flight). Triggers the
   *      catalog lookup on demand and hands the draft to
   *      `applyEditorDraftToPageDashboardByKeys`, which internally
   *      `forkJoin`s the catalog + per-page-dashboard load and
   *      resolves widget ids from whichever responds with usable
   *      data. The reactive effects in the constructor remain the
   *      primary source of truth — this fallback only fills the
   *      timing gap.
   */
  private _persistLayoutToBackend(
    draft: ReadonlyArray<LayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    // ── Fast path: same shape as Social Platform's apply flow.
    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: d.order,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    // ── Slow / fallback path: resolve `pageId` on demand and let
    // the by-keys variant figure out widget ids from whichever
    // source has them (catalog or existing dashboard). The
    // `getCatalogProducts()` request is normally cached by the
    // service, so this is essentially free on subsequent clicks.
    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe((catalog) => {
        const resolvedPageId =
          cachedPageId ??
          findCatalogPageId(catalog as any[], this.PAGE_KEY, productId);

        if (resolvedPageId == null) {
          // eslint-disable-next-line no-console
          console.warn(
            `[CampaignAdsStats] No catalog page found for "${this.PAGE_KEY}" ` +
            `(active product=${productId}). Verify the dashboards catalog ` +
            `exposes a page with one of the configured hints (e.g. ` +
            `"campaign ads", "campaign ad").`,
          );
          return;
        }

        // Cache for next click so the fast path kicks in.
        if (cachedPageId == null) {
          this.effectiveCatalogPageId.set(resolvedPageId);
        }

        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: resolvedPageId,
          productId,
          fallbackSourcePageId: resolvedPageId,
          draft: draft.map((d, idx) => ({
            key: String(d.id),
            sourcePageId: resolvedPageId,
            colSpan: this._normalizeColSpan(d.colSpan),
            visible: d.visible,
            customConfig: null,
            order: idx,
          })),
        });
      });
  }

  // ─── Editor: width chips (decrease / increase / labels) ─────────────────
  /** Steps a card down one width. Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: CampaignAdsStatsSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
  }

  increaseEditorCardColSpan(id: CampaignAdsStatsSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  /**
   * Auto-adjusts widths in the editor so every row fully spans the 12-col
   * grid. Delegates to the shared {@link computeLayoutEditorAutoWidthUpdates}
   * helper, which grows the smallest card in each leaky row (and shares the
   * leftover equally when multiple cards tie at the smallest width).
   */
  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
  }

  /** Whether clicking Auto Adjust Width would actually grow at least one card. */
  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(this.editorItems()).size > 0;
  }

  /**
   * Whether the minimize button should be active for this card. A card
   * cannot shrink below its designed default col-span — e.g. a card
   * designed for `col-8` can only step between 8 and 12.
   */
  canDecreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether `span` is below the designed default col-span for `id`. */
  private _isBelowDefaultColSpan(
    id: CampaignAdsStatsSectionId,
    span: number,
  ): boolean {
    const defaultSpan = this.DEFAULT_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }

  canIncreaseEditorCardColSpan(item: LayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  /** Tailwind col-span class for an editor tile inside the popup's 12-col grid. */
  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'col-span-4';
      case 6: return 'col-span-6';
      case 8: return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  /** Human-friendly label for a col-span value (Small / Medium / Large / Full). */
  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'Small';
      case 6: return 'Medium';
      case 8: return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }

  /* Swaps the dragged card with the card at the drop target so that ONLY those two tiles exchange positions in the array; every other card keeps its current array slot (no shuffle/cascade like CDK's default `moveItemInArray`). we changed the strategy from shift to pairwise-swap [event, { strategy: 'shift' });]*/
  onEditorCardDrop(event: CdkDragDrop<LayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'pairwise-swap',
    });
    if (next) this.editorItems.set(next);
  }
  // ─── Layout state private helpers ──────────────────────────────────────
  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<CampaignAdsStatsSectionId, SectionLayoutItem> =
      {} as Record<CampaignAdsStatsSectionId, SectionLayoutItem>;

    this.LAYOUT_SECTIONS.forEach((s, idx) => {
      const sid = s.id as CampaignAdsStatsSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, SectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    layout: Record<CampaignAdsStatsSectionId, SectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable (e.g. private mode) — silently ignore.
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  /**
   * Tailwind grid class for the Stats Overview card row. auto adjust css for - col-12, col-8, col-6, col-4  Class strings are returned as full literals (not template-built) so Tailwind's source scanner picks them up.
   */
  getStatsGridClass(): string {
    const count = this.businessContractsOverallStats.length;
    const span = this._normalizeColSpan(
      this.layoutMap()[CampaignAdsStatsSectionId.STATS_OVERVIEW]?.colSpan ?? 12,
    );
    const lgCols = this._statsLgColsClass(span, count);
    return `grid grid-cols-1 sm:grid-cols-2 ${lgCols}`;
  }

  /** Tailwind class for the Stats Overview loader placeholder. */
  getStatsLoaderClass(): string {
    const count = this.businessContractsOverallStats.length;
    const span = this._normalizeColSpan(
      this.layoutMap()[CampaignAdsStatsSectionId.STATS_OVERVIEW]?.colSpan ?? 12,
    );
    const lgSpan = this._statsLgLoaderSpanClass(span, count);
    return `h-[135px] col-span-1 sm:col-span-2 ${lgSpan} flex items-center justify-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700`;
  }

  /** Picks the `lg:grid-cols-*` class for the Stats Overview row. */
  private _statsLgColsClass(span: number, count: number): string {
    if (span === 4 || span === 6) return 'lg:grid-cols-2';
    if (span === 8) return 'lg:grid-cols-3';
    switch (count) {
      case 1: return 'lg:grid-cols-1';
      case 2: return 'lg:grid-cols-2';
      case 3: return 'lg:grid-cols-3';
      case 4: return 'lg:grid-cols-4';
      case 5: return 'lg:grid-cols-5';
      case 6: return 'lg:grid-cols-6';
      default: return 'lg:grid-cols-5';
    }
  }

  /** Picks the `lg:col-span-*` class for the Stats Overview loader. */
  private _statsLgLoaderSpanClass(span: number, count: number): string {
    if (span === 4 || span === 6) return 'lg:col-span-2';
    if (span === 8) return 'lg:col-span-3';
    switch (count) {
      case 1: return 'lg:col-span-1';
      case 2: return 'lg:col-span-2';
      case 3: return 'lg:col-span-3';
      case 4: return 'lg:col-span-4';
      case 5: return 'lg:col-span-5';
      case 6: return 'lg:col-span-6';
      default: return 'lg:col-span-5';
    }
  }

  updateChartColors() {
    const isDark = this.themeService.getTheme() === 'dark';

    this.colors = {
      // Enrolled
      data1: isDark ? '#a78bfa' : '#8b5cf6',
      // Enquiries
      data2: isDark ? '#818cf8' : '#6366f1',
      // Cost
      data3: isDark ? '#f472b6' : '#b927ff',
    };

    // Refresh legend colors
    this.customLegendData = [
      { label: 'Enrolled', color: this.colors.data1 },
      { label: 'Enquiries', color: this.colors.data2 },
      { label: 'Cost', color: this.colors.data3 },
    ];
  }

  tooltipData = [
    {
      label: 'Enrolled',
      key: 'enrolled',
    },
    {
      label: 'Enquiries',
      key: 'enquiries',
    },
    {
      label: 'Cost',
      key: 'cost',
    },
  ];

  colors: any = {
    data1: '#15B268',
    data2: '#0C66E8',
    data3: '#AD46FF',
  };

  monthlyActivityAnalysis: any[] = [];
  businessContractsOverallStats: any[] = [];
  isCardsLoading = signal<boolean>(false);


  updateCardsWithListTotals() {
    if (!this.businessContractsOverallStats.length || !this.latestEnquiryList.length) return;

    const totalEnquiries = this.latestEnquiryList.reduce((sum, item) => sum + (Number(item.total) || Number(item.enquiries) || 0), 0);
    const totalEnrolled = this.latestEnquiryList.reduce((sum, item) => sum + (Number(item.total_registered) || Number(item.enrolled) || 0), 0);
    const totalConvRatio = this.latestEnquiryList.reduce((sum, item) => sum + (Number(item.conversion_ratio) || Number(item.conversionRatio) || 0), 0);

    this.businessContractsOverallStats = this.businessContractsOverallStats.map(card => {
      if (card.key === 'enquiries') {
        return { ...card, value: totalEnquiries };
      }
      if (card.key === 'businessEnrolled') {
        return { ...card, value: totalEnrolled };
      }
      if (card.key === 'convRatio') {
        // Formatting conversion ratio to 2 decimal places if needed, or keeping as number
        return { ...card, value: Number(totalConvRatio.toFixed(2)) };
      }
      return card;
    });
  }

  loadOverallPerformance() {
    if (!this.isSectionActive(CampaignAdsStatsSectionId.STATS_OVERVIEW)) return;

    const productId = this.apiService.getSelectedProductId();
    if (!productId) return;

    const fetchGeneration = this.dataFetchGeneration;
    const fetchProductId = productId;

    this.isCardsLoading.set(true);

    const params: any = {
      productId: productId,
      startDate: dayjs(this.start_date).format('YYYY-MM'),
      endDate: dayjs(this.end_date).format('YYYY-MM')
    };

    this.apiService.get('/campaign-metrics/overall-performance', params).subscribe({
      next: (response: any) => {
        const currentProductId = this.apiService.getSelectedProductId();
        const isStale = fetchGeneration !== this.dataFetchGeneration
          || fetchProductId !== currentProductId;
        if (isStale) {
          this.isCardsLoading.set(false);
          return;
        }
        const data = response.data || response || {};
        const stats = data.currentPeriod?.stats || {};
        const growth = data.growth || {};

        this.businessContractsOverallStats = [
          {
            label: 'Allocated Budget',
            key: 'allocatedBudget',
            value: stats.budget || 0,
            difference: Number((growth.budget || 0).toFixed(2)),
            icon: 'wallet',
            bgClass: 'bg-[#AD46FF]',
            iconClass: 'text-white dark:text-surface-1',
            shadowClass: 'shadow-[0px_4px_12px_0px_#AD46FF80]',
            showDollarIcon: true,
          },
          {
            label: 'Funds Used',
            key: 'fundsUsed',
            value: stats.cost || 0,
            difference: Number((growth.cost || 0).toFixed(2)),
            icon: 'sparkles',
            bgClass: 'bg-[#0C66E8]',
            iconClass: 'text-white dark:text-surface-1',
            shadowClass: 'shadow-[0px_4px_12px_0px_#0C66E880]',
            showDollarIcon: true,
          },
          {
            label: 'Enquiries',
            key: 'enquiries',
            value: stats.enquiries || 0,
            difference: Number((growth.enquiries || 0).toFixed(2)),
            icon: 'messageSquareMore',
            bgClass: 'bg-[#FFA514]',
            iconClass: 'text-white dark:text-surface-1',
            shadowClass: 'shadow-[0px_4px_12px_0px_#FFA51480]',
            showDollarIcon: false,
          },
          {
            label: 'Business Enrolled',
            key: 'businessEnrolled',
            value: stats.enrolled || 0,
            difference: Number((growth.enrolled || 0).toFixed(2)),
            icon: 'building2',
            bgClass: 'bg-[#F05050]',
            iconClass: 'text-white dark:text-surface-1',
            shadowClass: 'shadow-[0px_4px_12px_0px_#F0505080]',
            showDollarIcon: false,
          },
          {
            label: 'Conversion. Ratio (%)',
            key: 'convRatio',
            value: stats.conversionRatio || 0,
            difference: Number((growth.conversionRatio || 0).toFixed(2)),
            icon: 'eye',
            bgClass: 'bg-[#38385B] dark:bg-white',
            iconClass: 'text-white dark:text-surface-1',
            shadowClass: 'shadow-[0px_4px_12px_0px_#0000001A]',
            showDollarIcon: false,
          },
        ];
        this.updateCardsWithListTotals();
        this.isCardsLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading overall performance', err);
        const resolvedMsg = this.resolveErrorMessage(err);
        this.toasterService.error(resolvedMsg);
        this.errorMessage.set(resolvedMsg);
        this.isCardsLoading.set(false);
        setTimeout(() => this.errorMessage.set(''), 5000);
      }
    });
  }

  loadCombinedMetrics() {
    if (
      !this.isSectionActive(
        CampaignAdsStatsSectionId.MOM_COMPARISON,
        CampaignAdsStatsSectionId.MONTHLY_ENQUIRIES,
        CampaignAdsStatsSectionId.STATS_OVERVIEW,
      )
    ) {
      return;
    }

    const productId = this.productId();
    if (!productId) return;

    const fetchGeneration = this.dataFetchGeneration;
    const fetchProductId = productId;

    this.isLoading.set(true);

    const productName = this.productConfigService.getProductName(productId);
    const config = this.productConfigService.getProductConfig(productName);
    const toolPath = config.reportToolsPath || 'visionboard/tools';

    const params: any = {
      startDate: dayjs(this.start_date).format('YYYY-MM-DD HH:mm:ss'),
      endDate: dayjs(this.end_date).format('YYYY-MM-DD HH:mm:ss')
    };

    // Special handling for Finance Mutual parameters
    if (productName.toLowerCase().includes('finance mutual')) {
      params.start_time = dayjs(this.start_date).format('YYYY-MM-DD');
      params.end_time = dayjs(this.end_date).format('YYYY-MM-DD');

    }

    const enquiryEndpoint = ['finance mutual', 'recuvery'].includes(productName.toLowerCase()) ? 'enquiry' : 'prism_enquiry';
    const obs1 = this.getEnquiryObservable(toolPath, enquiryEndpoint, params, config);

    const costParams = {
      productId: productId,
      sortBy: 'date',
      sortOrder: 'desc',
      page: 1,
      limit: 20,
      startDate: dayjs(this.start_date).format('YYYY-MM'),
      endDate: dayjs(this.end_date).format('YYYY-MM'),
      unit: this.unit(),
    };

    const obs2 = this.apiService.get('/campaign-metrics', costParams).pipe(
      map((res: any) => {
        if (!res) return [];
        if (res.data && Array.isArray(res.data)) {
          return res.data;
        }
        if (res.data?.campaignMetrics && Array.isArray(res.data.campaignMetrics)) {
          return res.data.campaignMetrics;
        }
        if (Array.isArray(res)) {
          return res;
        }
        return res?.campaignMetrics || res?.data?.list || res?.data?.data || res?.data || [];
      }),
      catchError(err => {
        console.error('Error fetching cost metrics', err);
        return of([]);
      })
    );

    forkJoin({ enquiryData: obs1, costData: obs2 }).subscribe({
      next: ({ enquiryData, costData }) => {
        const isStale = fetchGeneration !== this.dataFetchGeneration
          || fetchProductId !== this.productId();
        if (isStale) {
          this.isLoading.set(false);
          return;
        }
        this.latestEnquiryList = Array.isArray(enquiryData) ? enquiryData : [];
        this.updateCardsWithListTotals();
        this.processCampaignMetrics(this.latestEnquiryList, Array.isArray(costData) ? costData : []);
        this.populateChartWithLatestData(enquiryData as any[], costData as any[]);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading combined metrics', err);
        const resolvedMsg = this.resolveErrorMessage(err);
        this.toasterService.error(resolvedMsg);
        this.errorMessage.set(resolvedMsg);
        this.isLoading.set(false);
        setTimeout(() => this.errorMessage.set(''), 5000);
      }
    });
  }

  private getEnquiryObservable(toolPath: string, endpoint: string, params: any, config: any): any {
    return this.apiService.post(`${toolPath}/${endpoint}`, params, {
      addAccessToken: config.requiresAccessToken,
      productServerName: config.productServerName,
      withCredentials: false
    }).pipe(
      map((res: any) => res.result || res.campaignMetrics || res.list || []),
      catchError(err => {
        // Handle token refresh for authenticated products
        if (
          config.requiresAccessToken &&
          (err?.err === 'Invalid or expired access token' ||
            err?.message === 'Invalid or expired access token' ||
            err?.error === 'Invalid or expired access token')
        ) {
          return this.productConfigService.generateNewRefreshToken(this.productId()).pipe(
            switchMap(() => this.getEnquiryObservable(toolPath, endpoint, params, config))
          );
        }
        console.error('Error fetching enquiry data', err);
        return of([]);
      })
    );
  }

  parseBackendDate(dateStr: string, unit: string): dayjs.Dayjs {
    if (!dateStr) return dayjs();
    if (typeof dateStr === 'string') {
      if (dateStr.includes(':')) {
        const parts = dateStr.split(':');
        return dayjs(parts[0]);
      }
      if (/^\d{4}-Q\d$/i.test(dateStr)) {
        const parts = dateStr.split('-Q');
        const year = parseInt(parts[0], 10);
        const quarter = parseInt(parts[1], 10);
        return dayjs().year(year).month((quarter - 1) * 3).startOf('month');
      }
      if (/^\d{4}$/.test(dateStr)) {
        return dayjs().year(parseInt(dateStr, 10)).startOf('year');
      }
    }
    return dayjs(dateStr);
  }

  getUnitKey(date: dayjs.Dayjs, unit: string): string {
    switch (unit) {
      case 'quarter':
        return date.startOf('quarter').format('YYYY-MM-DD');
      case 'year':
        return date.startOf('year').format('YYYY-MM-DD');
      case 'month':
      default:
        return date.startOf('month').format('YYYY-MM');
    }
  }

  getUnitLabels(date: dayjs.Dayjs, unit: string): { month_short: string; label: string } {
    switch (unit) {
      case 'quarter':
        const quarterNum = Math.floor(date.month() / 3) + 1;
        return {
          month_short: `Q${quarterNum}`,
          label: `Q${quarterNum} ${date.format('YYYY')}`
        };
      case 'year':
        return {
          month_short: date.format('YYYY'),
          label: date.format('YYYY')
        };
      case 'month':
      default:
        return {
          month_short: date.format('MMM'),
          label: date.format('MMM YYYY')
        };
    }
  }

  populateChartWithLatestData(data: any[], costData: any[] = []) {
    if ((!data || !data.length) && (!costData || !costData.length)) {
      this.processedData1 = [];
      this.processedData2 = [];
      this.processedData3 = [];
      return;
    }

    const currentUnit = this.unit();

    const parseValue = (val: any) => {
      if (val === 'NA' || val === 'N/A' || val === null || val === undefined) return 0;
      const parsed = Number(val);
      return isNaN(parsed) ? 0 : parsed;
    };

    const costMap = new Map<string, number>();
    costData.forEach(c => {
      const dateVal = c.period || c.date;
      if (dateVal) {
        const d = this.parseBackendDate(dateVal, currentUnit);
        const key = this.getUnitKey(d, currentUnit);
        costMap.set(key, (costMap.get(key) || 0) + parseValue(c.cost));
      }
    });

    const enquiryMap = new Map<string, { enrolled: number; enquiries: number; date: dayjs.Dayjs }>();
    data.forEach(item => {
      const dateStr = item.inq_date || item.date || item.period;
      if (dateStr) {
        const d = this.parseBackendDate(dateStr, currentUnit);
        const key = this.getUnitKey(d, currentUnit);

        const existing = enquiryMap.get(key) || { enrolled: 0, enquiries: 0, date: d };
        existing.enrolled += (Number(item.total_registered) || Number(item.enrolled) || 0);
        existing.enquiries += (Number(item.total) || Number(item.enquiries) || 0);
        enquiryMap.set(key, existing);
      }
    });

    costMap.forEach((cost, key) => {
      if (!enquiryMap.has(key)) {
        const d = dayjs(key);
        enquiryMap.set(key, { enrolled: 0, enquiries: 0, date: d });
      }
    });

    const sortedKeys = Array.from(enquiryMap.keys()).sort((a, b) => {
      return dayjs(a).unix() - dayjs(b).unix();
    });

    const formattedData = sortedKeys.map(key => {
      const item = enquiryMap.get(key)!;
      const cost = costMap.get(key) || 0;
      const { month_short, label } = this.getUnitLabels(item.date, currentUnit);

      return {
        start: item.date.format('YYYY-MM-DD'),
        month_short: month_short,
        label: label,
        enrolled: item.enrolled,
        enquiries: item.enquiries,
        cost: cost
      };
    });

    this.processedData1 = formattedData.map(d => ({ ...d, value: d.enrolled }));
    this.processedData2 = formattedData.map(d => ({ ...d, value: d.enquiries }));
    this.processedData3 = formattedData.map(d => ({ ...d, value: d.cost }));

    this.updateChartColors();
  }

  onDateRangeChange(event: DateRangePickerOutput) {

    const {
      start_date,
      end_date,
      unit,
      previous_start_date,
      previous_end_date
    } = event;

    const parseDate = (dStr: string) => {
      if (!dStr) return dayjs();
      if (dStr.includes('T') || dStr.includes('Z')) {
        return dayjs.utc(dStr).tz(this.LA_TIMEZONE);
      }
      return dayjs(dStr);
    };

    this.start_date = parseDate(start_date).format('YYYY-MM-DD');
    this.end_date = parseDate(end_date).format('YYYY-MM-DD');
    this.unit.set(unit || 'month');

    // Handle comparison if needed (CampaignAdsStats might need refactoring if it supports MoM comparison in charts)
    this.isPrevious = false; // Reset for main load

    // === Dynamic Label Logic ===
    const s = dayjs(this.start_date);
    const e = dayjs(this.end_date);
    const now = dayjs();

    const diffMo = e.diff(s, 'month', true);
    if (Math.abs(diffMo - 1) < 0.2) {
      if (s.month() === now.subtract(1, 'month').month()) {
        this.metricsLabel = 'Last Month Campaign Metrics';
      } else {
        this.metricsLabel = 'Last Month Campaign Metrics';
      }
    } else if (Math.abs(diffMo - 3) < 0.5) {
      this.metricsLabel = 'Last 3 Months Campaign Metrics';
    } else if (Math.abs(diffMo - 6) < 0.5) {
      this.metricsLabel = 'Last 6 Months Campaign Metrics';
    } else if (Math.abs(diffMo - 12) < 0.5) {
      this.metricsLabel = 'Last Year Campaign Metrics';
    } else if (Math.abs(diffMo - 24) < 0.5) {
      this.metricsLabel = 'Last 2 Years Campaign Metrics';
    } else if (Math.abs(diffMo - 36) < 0.5) {
      this.metricsLabel = 'Last 3 Years Campaign Metrics';
    } else if (Math.abs(diffMo - 60) < 0.5) {
      this.metricsLabel = 'Last 5 Years Campaign Metrics';
    } else {
      if (s.year() !== e.year()) {
        this.metricsLabel = `${s.format('MMM YYYY')} - ${e.format('MMM YYYY')} Campaign Metrics`;
      } else {
        this.metricsLabel = `${s.format('MMM')} - ${e.format('MMM')} Campaign Metrics`;
      }
    }

    // Trigger APIs
    this.loadCombinedMetrics();
    this.loadOverallPerformance();
  }

  processCampaignMetrics(data: any[], manualData: any[] = []) {
    const currentUnit = this.unit();

    const parseValue = (val: any) => {
      if (val === 'NA' || val === 'N/A' || val === null || val === undefined) return 0;
      const parsed = Number(val);
      return isNaN(parsed) ? 0 : parsed;
    };

    const manualMap = new Map<string, { cost: number; budget: number; id?: any }>();
    manualData.forEach(m => {
      const dateVal = m.period || m.date;
      if (dateVal) {
        const d = this.parseBackendDate(dateVal, currentUnit);
        const key = this.getUnitKey(d, currentUnit);
        const existing = manualMap.get(key) || { cost: 0, budget: 0 };
        existing.cost += parseValue(m.cost);
        existing.budget += parseValue(m.budget);
        if (m.id) existing.id = m.id;
        manualMap.set(key, existing);
      }
    });

    const groupedEnquiries = new Map<string, { total: number; total_registered: number; date: dayjs.Dayjs }>();
    data.forEach(item => {
      const dateStr = item.inq_date || item.date || item.period;
      if (dateStr) {
        const d = this.parseBackendDate(dateStr, currentUnit);
        const key = this.getUnitKey(d, currentUnit);

        const existing = groupedEnquiries.get(key) || { total: 0, total_registered: 0, date: d };
        existing.total += (Number(item.total) || Number(item.enquiries) || 0);
        existing.total_registered += (Number(item.total_registered) || Number(item.enrolled) || 0);
        groupedEnquiries.set(key, existing);
      }
    });

    manualMap.forEach((val, key) => {
      if (!groupedEnquiries.has(key)) {
        const d = dayjs(key);
        groupedEnquiries.set(key, { total: 0, total_registered: 0, date: d });
      }
    });

    const sortedKeys = Array.from(groupedEnquiries.keys()).sort((a, b) => {
      return dayjs(a).unix() - dayjs(b).unix();
    });

    this.monthlyActivityAnalysis = sortedKeys.map(key => {
      const item = groupedEnquiries.get(key)!;
      const manualItem = manualMap.get(key) || { cost: 0, budget: 0 };

      const cost = manualItem.cost || 0;
      const budget = manualItem.budget || 0;

      let convRatio = 0;
      if (item.total > 0) {
        convRatio = Number(((item.total_registered / item.total) * 100).toFixed(2));
      }

      const { label } = this.getUnitLabels(item.date, currentUnit);

      return {
        [currentUnit]: label,
        Enquiries: item.total,
        Enrolled: item.total_registered,
        Cost: this.formatCurrency(cost),
        Budget: this.formatCurrency(budget),
        'Conv. Ratio': `${convRatio}%`,
        action: this.sanitizer.bypassSecurityTrustHtml(`<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="cursor-pointer text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"><path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"/></svg>`),
        _original: {
          inq_date: item.date.format('YYYY-MM-DD'),
          date: item.date.format('YYYY-MM-DD'),
          period: label,
          total: item.total,
          total_registered: item.total_registered,
          cost,
          budget,
          id: manualItem.id
        }
      };
    });

    this.updateTableHeaders();
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  updateTableHeaders() {
    if (!this.monthlyActivityAnalysis || !this.monthlyActivityAnalysis.length) {
      this.headers.set([]);
      return;
    }
    const firstItem = this.monthlyActivityAnalysis[0];

    const newHeaders = Object.keys(firstItem)
      .filter(key => key !== '_original' && key !== 'action')
      .map((key, index) => {
        let align: 'text-left' | 'text-center' | 'text-right';

        if (index === 0) align = 'text-left';
        else align = 'text-center';

        return {
          label: this.formatHeaderLabel(key),
          key,
          align,
        } as TableHeader;
      });

    newHeaders.push({
      label: 'Action',
      key: 'action',
      align: 'text-center'
    });

    this.headers.set(newHeaders);
  }

  onEditStats(row: any) {
    const item = row._original;
    if (!item) return;

    const dateStr = item.inq_date || item.date || item.period;
    let dateVal = null;
    let formattedDate = '';
    if (dateStr) {
      const d = dayjs(dateStr);
      if (d.isValid()) {
        dateVal = { year: d.year(), month: d.month() + 1 };
        formattedDate = d.format('YYYY-MM');

        if (this.unit() === 'quarter') {
          const year = d.year();
          const quarterStartMonth = Math.floor(d.month() / 3) * 3 + 1;
          const quarterEndMonth = quarterStartMonth + 2;
          this.dropdownMinYear = year;
          this.dropdownMaxYear = year;
          this.dropdownMinMonth = quarterStartMonth;
          this.dropdownMaxMonth = quarterEndMonth;
        } else if (this.unit() === 'year') {
          const year = d.year();
          this.dropdownMinYear = year;
          this.dropdownMaxYear = year;
          this.dropdownMinMonth = 1;
          this.dropdownMaxMonth = 12;
        } else {
          this.dropdownMinYear = 2018;
          this.dropdownMaxYear = this.currentYear;
          this.dropdownMinMonth = undefined;
          this.dropdownMaxMonth = this.currentMonth;
        }
      }
    }

    this.statsForm.patchValue({
      id: item.id || null,
      date: dateVal,
      cost: item.cost || 0,
      budget: item.budget || 0
    }, { emitEvent: false });

    if (this.unit() === 'month') {
      this.statsForm.get('date')?.disable();
    } else {
      this.statsForm.get('date')?.enable();
    }

    this.isEditing.set(true);

    const productPid = this.apiService.getSelectedProductPid();
    if (productPid && formattedDate) {
      this.isLoading.set(true);
      this.apiService.get('/campaign-metrics/by-date', {
        startDate: formattedDate,
        endDate: formattedDate,
        productPid: productPid
      }).subscribe({
        next: (res: any) => {
          this.isLoading.set(false);
          const data = res?.data || res;
          const record = Array.isArray(data) ? data[0] : (data?.campaignMetrics?.[0] || data?.[0] || data);

          if (record && record.pid) {
            this.statsForm.patchValue({ id: record.pid }, { emitEvent: false });
          } else {
            this.statsForm.patchValue({ id: null }, { emitEvent: false });
          }
          this.panelOpen.set(true);
        },
        error: (err) => {
          this.isLoading.set(false);
          console.error('Error checking campaign metrics by date', err);
          this.statsForm.patchValue({ id: null }, { emitEvent: false });
          this.panelOpen.set(true);
        }
      });
    } else {
      this.panelOpen.set(true);
    }
  }

  private formatHeaderLabel(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private formatStartDate(
    startDate: string,
    endDate: string,
    unit: string
  ): string {
    if (unit === 'quarter') {
      return `${dayjs(startDate).format('MMM YY')} - ${dayjs(endDate).format(
        'MMM YY'
      )}`;
    } else if (unit === 'year') {
      return `${dayjs(startDate).format('YYYY')}`;
    } else if (unit === 'month') {
      return `${dayjs(startDate).format('MMM YYYY')}`;
    }
    return dayjs(startDate).format('DD MMM');
  }
}